import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CardRenderer } from "@/components/card/CardRenderer";
import { api, BACKEND_URL, fileUrl, publicCardUrl, vcardUrl, copyText } from "@/lib/api";
import { fullName } from "@/lib/cardUtils";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function applySeo(card) {
  const i = card.identity;
  const name = fullName(i) || i.company;
  const title = card.settings.seo_title || [name, i.role, i.company !== name ? i.company : ""].filter(Boolean).join(" · ");
  const desc = card.settings.seo_description || i.bio || `Contatti di ${name}`;
  const img = card.settings.og_image_url || i.cover_url || i.avatar_url;
  document.title = title;
  setMeta("name", "description", desc);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:url", publicCardUrl(card.slug));
  setMeta("property", "og:type", "profile");
  if (img) setMeta("property", "og:image", fileUrl(img));
  setMeta("name", "theme-color", card.style.colors.background);
}

const Message = ({ title, text }) => (
  <div className="grid min-h-screen place-items-center bg-[#FAFAF9] px-6 text-center" data-testid="public-card-message">
    <div><div className="font-display text-4xl">{title}</div><p className="mt-3 text-sm text-zinc-500">{text}</p></div>
  </div>
);

export default function PublicCard() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const src = sp.get("s") === "qr" ? "qr" : "direct";
  const [card, setCard] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    const key = `dcs_v_${slug}`;
    const first = !sessionStorage.getItem(key);
    if (first) sessionStorage.setItem(key, "1");
    api.get(`/public/cards/${slug}`, { params: { track: first ? 1 : 0, src } })
      .then(({ data }) => {
        if (data.redirect) return nav(`/c/${data.redirect}${location.search}`, { replace: true });
        setCard(data);
        applySeo(data);
        setState("ok");
      })
      .catch((e) => setState(e.response?.status === 410 ? "disabled" : "notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const track = (type, label) => {
    fetch(`${BACKEND_URL}/api/public/events`, {
      method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: card.slug, type, label: label || "", source: src }),
    }).catch(() => {});
  };

  const onSave = () => {
    window.location.href = vcardUrl(card.slug, src);
    toast("File contatto in download", { description: "Aprilo per confermare l'aggiunta alla rubrica del telefono." });
  };

  const onShare = async () => {
    const url = publicCardUrl(card.slug);
    track("share", "share");
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    await copyText(url);
    toast.success("Link copiato negli appunti");
  };

  if (state === "loading") return <div className="min-h-screen bg-[#FAFAF9]" />;
  if (state === "notfound") return <Message title="Card non trovata" text="L'indirizzo potrebbe essere errato o la card non è più pubblicata." />;
  if (state === "disabled") return <Message title="Card non disponibile" text="Questa digital card è momentaneamente disattivata." />;
  return (
    <div className="min-h-screen" style={{ background: card.style.colors.background }} data-testid="public-card-page">
      <CardRenderer card={card} onSave={onSave} onShare={onShare} track={track} fixedBg />
    </div>
  );
}
